import React, { useState } from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import Button from "./Button";
import { useNavigate } from "react-router-dom";

interface ReplayPopoverProps {
  adventureId: number;
  replayId?: string;
  replayUrl?: string;
  disabled?: boolean;
  children: React.ReactElement;
  placement?: "top" | "bottom" | "left" | "right";
  onReplaySelect?: (type: "legacy" | "3d") => void;
}

const ReplayPopover: React.FC<ReplayPopoverProps> = ({
  adventureId,
  replayId,
  replayUrl,
  disabled = false,
  children,
  placement = "top",
  onReplaySelect,
}) => {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);

  // If there's no replayUrl, handle directly without popover
  const handleDirectReplay = () => {
    if (replayId) {
      window.location.href = `/battle/treasure/${encodeURIComponent(replayId)}`;
      onReplaySelect?.("legacy");
    }
  };

  const handleLegacyReplay = () => {
    if (replayId) {
      window.location.href = `/battle/treasure/${encodeURIComponent(replayId)}`;
      onReplaySelect?.("legacy");
    }
    setShow(false);
  };

  const handle3DReplay = () => {
    if (replayUrl) {
      navigate(`/adventure/replay/${adventureId}`);
      onReplaySelect?.("3d");
    }
    setShow(false);
  };

  // If there's no replayUrl, handle directly without showing popover
  if (!replayUrl) {
    return React.cloneElement(children, {
      disabled: disabled || !replayId,
      onClick: disabled
        ? undefined
        : (e: React.MouseEvent) => {
            // Call the original onClick if it exists
            if (children.props.onClick) {
              children.props.onClick(e);
            }
            // Then handle our direct replay logic
            if (!e.defaultPrevented) {
              handleDirectReplay();
            }
          },
    });
  }

  const popover = (
    <Popover id={`replay-popover-${adventureId}`} className="replay-popover">
      <Popover.Header as="h6" className="text-center py-2">
        <i className="fas fa-play me-2"></i>
        Choose Replay Type
      </Popover.Header>
      <Popover.Body className="p-2 bg-paper">
        <div className="d-grid gap-2">
          <Button
            variant="wide-primary"
            size="small"
            onClick={handle3DReplay}
            disabled={!replayUrl}
            className="d-flex align-items-center justify-content-center"
          >
            <i className="fas fa-cube me-2"></i>
            3D Replay
          </Button>
          <Button
            variant="wide-secondary"
            size="small"
            onClick={handleLegacyReplay}
            disabled={!replayId}
            className="d-flex align-items-center justify-content-center"
          >
            <i className="fas fa-history me-2"></i>
            Legacy Replay
          </Button>
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger="click"
      placement={placement}
      overlay={popover}
      show={show}
      onToggle={(nextShow) => setShow(nextShow)}
      rootClose
    >
      <div>
        {React.cloneElement(children, {
          disabled: disabled || (!replayUrl && !replayId),
          onClick: disabled
            ? undefined
            : (e: React.MouseEvent) => {
                // Call the original onClick if it exists
                if (children.props.onClick) {
                  children.props.onClick(e);
                }
                // Then handle our popover logic
                if (!e.defaultPrevented) {
                  e.preventDefault();
                  e.stopPropagation();
                  setShow(!show);
                }
              },
        })}
      </div>
    </OverlayTrigger>
  );
};

export default ReplayPopover;
